/**
 * FormBuilder - Main 3-panel form builder layout.
 *
 * Left: Field library (collapsible)
 * Center: Canvas with sections and draggable controls
 * Right: Properties panel for the selected element
 * Top bar: form name, save, preview toggle, undo/redo
 */

import { Can } from '@/domains/access-control';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui';
import { ArrowLeft, Eye, Hammer, Redo, Save, Undo } from 'lucide-react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import FormBuilderCanvas from './FormBuilderCanvas';
import FormBuilderLibrary from './FormBuilderLibrary';
import FormBuilderPreview from './FormBuilderPreview';
import FormBuilderProperties from './FormBuilderProperties';
import { withRendererFlagForLayout, withUpdatedControl, withUpdatedSection } from './builder-utils';
import { useFormBuilderState } from './useFormBuilderState';

export default function FormBuilder() {
  const {
    form,
    set,
    undo,
    redo,
    canUndo,
    canRedo,
    navigate,
    activeView,
    setActiveView,
    libraryCollapsed,
    setLibraryCollapsed,
    selectedCanvas,
    setSelectedCanvas,
    selectedTarget,
    setSelectedTarget,
    pendingDelete,
    setPendingDelete,
    templates,
    formTypeOptions,
    selectedSection,
    selectedControl,
    isSaving,
    handleAddSection,
    handleAddControl,
    handleAddFromLibrary,
    handleMoveControl,
    handleMoveControlToSection,
    handleDuplicateSection,
    handleRenameSection,
    handleDeleteConfirm,
    handleSave,
  } = useFormBuilderState();

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex flex-col h-full">
        {/* Top bar */}
        <div className="flex items-center justify-between py-3 flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/settings/form-builder')}
              title="Back to form list"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h2 className="text-lg font-bold text-zinc-900">{form.name || 'Untitled Form'}</h2>
              <p className="text-xs text-zinc-500">
                {form.sections.length} {form.sections.length === 1 ? 'section' : 'sections'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex bg-zinc-100 rounded-md p-0.5">
              <button
                type="button"
                onClick={() => setActiveView('builder')}
                className={cn(
                  'px-3 py-1 text-xs font-medium rounded transition-colors',
                  activeView === 'builder'
                    ? 'bg-white text-zinc-900 shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-700',
                )}
              >
                <Hammer className="w-3.5 h-3.5 inline mr-1" />
                Builder
              </button>
              <button
                type="button"
                onClick={() => setActiveView('preview')}
                className={cn(
                  'px-3 py-1 text-xs font-medium rounded transition-colors',
                  activeView === 'preview'
                    ? 'bg-white text-zinc-900 shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-700',
                )}
              >
                <Eye className="w-3.5 h-3.5 inline mr-1" />
                Preview
              </button>
            </div>

            <Button variant="outline" size="sm" onClick={undo} disabled={!canUndo} title="Undo">
              <Undo className="w-3.5 h-3.5" />
            </Button>
            <Button variant="outline" size="sm" onClick={redo} disabled={!canRedo} title="Redo">
              <Redo className="w-3.5 h-3.5" />
            </Button>
            {/* Save persists a form definition. Gate on the action that matches the operation:
                `update` when editing an existing form, `create` when saving a new one — a
                create-only role must NOT be able to save edits to an existing form (WRK-5669). */}
            <Can action={form.id ? 'update' : 'create'} subject="Form">
              <Button size="sm" onClick={handleSave} disabled={isSaving}>
                <Save className="w-3.5 h-3.5 mr-1" />
                {isSaving ? 'Saving...' : form.id ? 'Save' : 'Create'}
              </Button>
            </Can>
          </div>
        </div>

        {/* Content area */}
        {activeView === 'builder' ? (
          <div className="flex flex-1 overflow-hidden gap-1.5">
            {/* Left: Field library */}
            <div
              className={cn(
                'flex-shrink-0 transition-all duration-200',
                libraryCollapsed ? 'w-12' : 'w-56',
              )}
            >
              <FormBuilderLibrary
                collapsed={libraryCollapsed}
                onToggleCollapse={() => setLibraryCollapsed(!libraryCollapsed)}
                onAddField={handleAddFromLibrary}
                configTemplates={templates.length > 0 ? templates : undefined}
              />
            </div>

            {/* Center: Canvas */}
            <div className="flex-1 min-w-0 overflow-hidden">
              <FormBuilderCanvas
                sections={form.sections}
                selected={selectedCanvas}
                onSelectSection={(sectionIndex) => {
                  setSelectedCanvas({ sectionIndex, controlIndex: null });
                  setSelectedTarget({ type: 'section', sectionIndex });
                }}
                onSelectControl={(sectionIndex, controlIndex) => {
                  setSelectedCanvas({ sectionIndex, controlIndex });
                  setSelectedTarget({ type: 'control', sectionIndex, controlIndex });
                }}
                onAddSection={handleAddSection}
                onDeleteSection={(si) => setPendingDelete({ type: 'section', sectionIndex: si })}
                onDeleteControl={(si, ci) =>
                  setPendingDelete({ type: 'control', sectionIndex: si, controlIndex: ci })
                }
                onAddControl={handleAddControl}
                onMoveControl={handleMoveControl}
                onMoveControlToSection={handleMoveControlToSection}
                onDuplicateSection={handleDuplicateSection}
                onRenameSection={handleRenameSection}
              />
            </div>

            {/* Right: Properties */}
            <div className="w-72 flex-shrink-0 overflow-hidden">
              <FormBuilderProperties
                form={form}
                target={selectedTarget}
                formTypeOptions={formTypeOptions}
                selectedSection={selectedSection ?? null}
                selectedControl={selectedControl ?? null}
                onChangeTarget={(next) => {
                  setSelectedTarget(next);
                  if (next.type === 'section' && selectedCanvas) {
                    setSelectedCanvas({ sectionIndex: next.sectionIndex, controlIndex: null });
                  }
                }}
                onUpdateForm={(updates) => set(withRendererFlagForLayout({ ...form, ...updates }))}
                onUpdateSection={(updates) =>
                  selectedCanvas
                    ? set(
                        withUpdatedSection(form, selectedCanvas.sectionIndex, (s) => ({
                          ...s,
                          ...updates,
                        })),
                      )
                    : undefined
                }
                onUpdateControl={(updates) =>
                  selectedCanvas && selectedCanvas.controlIndex !== null
                    ? set(
                        withUpdatedControl(
                          form,
                          selectedCanvas.sectionIndex,
                          selectedCanvas.controlIndex,
                          (c) => ({ ...c, ...updates }),
                        ),
                      )
                    : undefined
                }
                onReplaceControl={(next) =>
                  selectedCanvas && selectedCanvas.controlIndex !== null
                    ? set(
                        withUpdatedControl(
                          form,
                          selectedCanvas.sectionIndex,
                          selectedCanvas.controlIndex,
                          () => next,
                        ),
                      )
                    : undefined
                }
              />
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6 bg-zinc-50">
            <FormBuilderPreview form={form} />
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={Boolean(pendingDelete)} onOpenChange={() => setPendingDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm deletion</DialogTitle>
            <DialogDescription>
              This action cannot be undone. Are you sure you want to delete this{' '}
              {pendingDelete?.type === 'section' ? 'section' : 'field'}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DndProvider>
  );
}
